import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NavBar from "components/shared/NavBar";
import Footer from "components/landing/Footer";
import oLogo from 'assets/images/png/logo.png';

export default function TermsAndConditions() {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return <>
    <NavBar />
    <main className="pt-24 pb-16 px-6 md:px-20 max-w-4xl mx-auto text-gray-700 dark:text-gray-300">

      <section className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-8">
        <img src={oLogo} alt="BrewControl logo" className="w-16 p-2 bg-primary-500 dark:bg-primary-dark rounded-full" />
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Términos y Condiciones</h1>
          <p className="text-sm text-gray-500 mt-1">Última actualización: mayo de 2026</p>
        </div>
      </section>

      <p className="mb-6 leading-relaxed">
        Los presentes Términos y Condiciones regulan el acceso y uso de la plataforma <strong>BrewControl</strong>,
        un servicio de software de gestión cervecera disponible como aplicación móvil y panel web administrativo.
        Al registrarse y utilizar el servicio, el usuario acepta en su totalidad las condiciones aquí descritas.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">1. Descripción del servicio</h2>
      <p className="mb-4 leading-relaxed">
        BrewControl es una plataforma de gestión para cervecerías artesanales que permite administrar fermentadores,
        barriles, lotes de producción, clientes, facturas y más. El servicio se ofrece en dos modalidades:
      </p>
      <ul className="list-disc ml-6 mb-4 space-y-2">
        <li><strong>Panel administrativo web</strong>: accesible desde cualquier navegador en <a href="https://admin.brewcontrol.uy" className="underline text-primary-500" target="_blank" rel="noreferrer">admin.brewcontrol.uy</a>.</li>
        <li><strong>Aplicación móvil</strong>: disponible para Android en Google Play y para iOS en App Store.</li>
      </ul>
      <p className="mb-4 leading-relaxed">
        El servicio incluye funcionalidades como el seguimiento de barriles mediante códigos QR, gestión de
        fermentadores, registro de clientes, emisión de facturas y reportes de producción, según el plan contratado.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">2. Registro y contratación</h2>
      <p className="mb-4 leading-relaxed">
        Para acceder al servicio, el usuario debe registrarse en <a href="https://brewcontrol.uy/register" className="underline text-primary-500">brewcontrol.uy/register</a> proporcionando
        nombre, correo electrónico y contraseña. El registro es gratuito e incluye acceso al plan <strong>Free</strong>
        de forma inmediata, sin costo ni compromiso.
      </p>
      <p className="mb-4 leading-relaxed">
        Para acceder a funcionalidades adicionales, el usuario puede contratar un plan pago desde el panel
        administrativo, seleccionando la modalidad mensual o anual. El pago se procesa de forma segura a través
        de las pasarelas habilitadas en la plataforma.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">3. Planes y precios</h2>
      <p className="mb-4 leading-relaxed">
        BrewControl ofrece los siguientes planes:
      </p>
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800 text-left">
              <th className="px-4 py-2 border border-gray-200 dark:border-gray-700">Plan</th>
              <th className="px-4 py-2 border border-gray-200 dark:border-gray-700">Mensual</th>
              <th className="px-4 py-2 border border-gray-200 dark:border-gray-700">Anual (10% desc.)</th>
              <th className="px-4 py-2 border border-gray-200 dark:border-gray-700">Límite barriles</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Free</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Gratis</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">—</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">20</td>
            </tr>
            <tr className="bg-gray-50 dark:bg-gray-900">
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Bronce</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 8/mes</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 86/año</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">150</td>
            </tr>
            <tr>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Plata</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 15/mes</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 162/año</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">300</td>
            </tr>
            <tr className="bg-gray-50 dark:bg-gray-900">
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Oro</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 25/mes</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">USD 270/año</td>
              <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">600</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mb-4 leading-relaxed text-sm text-gray-500">
        Los precios están expresados en dólares estadounidenses (USD). BrewControl se reserva el derecho de
        actualizar los precios con previo aviso de al menos 30 días.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">4. Facturación y renovación</h2>
      <p className="mb-4 leading-relaxed">
        Los planes pagos se facturan de forma anticipada, ya sea mensual o anualmente según la opción elegida.
        La suscripción se renueva automáticamente al finalizar cada período, salvo que el usuario la cancele
        antes de la fecha de vencimiento desde el panel administrativo.
      </p>
      <p className="mb-4 leading-relaxed">
        En caso de no efectuarse el pago en la fecha correspondiente, el acceso a las funcionalidades del plan
        pago quedará suspendido y la cuenta pasará al modo Free hasta que se regularice la situación.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">5. Cancelación</h2>
      <p className="mb-4 leading-relaxed">
        El usuario puede cancelar su suscripción en cualquier momento desde el panel administrativo, en la
        sección <strong>Suscripción</strong>. La cancelación tiene efecto al finalizar el período ya abonado;
        no se realizan reembolsos por períodos parciales.
      </p>
      <p className="mb-4 leading-relaxed">
        Tras la cancelación, la cuenta conserva acceso al plan Free y los datos del usuario se mantienen
        disponibles por un período de 90 días, luego del cual podrán ser eliminados de forma permanente.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">6. Uso aceptable</h2>
      <p className="mb-4 leading-relaxed">
        El usuario se compromete a utilizar BrewControl únicamente para fines lícitos relacionados con la
        gestión de su cervecería. Queda prohibido compartir credenciales de acceso, intentar acceder a cuentas
        de terceros, realizar ingeniería inversa sobre la plataforma o utilizarla para actividades fraudulentas.
      </p>
      <p className="mb-4 leading-relaxed">
        BrewControl se reserva el derecho de suspender o cancelar cuentas que incumplan estas condiciones,
        sin obligación de reembolso.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">7. Disponibilidad del servicio</h2>
      <p className="mb-4 leading-relaxed">
        BrewControl procura mantener el servicio disponible de forma continua, pero no garantiza disponibilidad
        ininterrumpida. Pueden producirse interrupciones por mantenimiento, actualizaciones o causas de fuerza
        mayor. En ningún caso BrewControl será responsable por daños derivados de la falta de disponibilidad
        temporal del servicio.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">8. Propiedad intelectual</h2>
      <p className="mb-4 leading-relaxed">
        Todos los derechos sobre el software, diseño, marca y contenidos de BrewControl son propiedad exclusiva
        de sus creadores. El usuario recibe una licencia de uso personal, intransferible y no exclusiva,
        limitada al período de vigencia de su suscripción.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">9. Protección de datos</h2>
      <p className="mb-4 leading-relaxed">
        El tratamiento de los datos personales del usuario se rige por nuestra{" "}
        <Link to="/policy" className="underline text-primary-500">Política de Privacidad</Link>.
        BrewControl cumple con la normativa vigente en materia de protección de datos personales en Uruguay
        (Ley N° 18.331).
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">10. Modificaciones</h2>
      <p className="mb-4 leading-relaxed">
        BrewControl puede modificar estos Términos y Condiciones en cualquier momento. Los cambios serán
        notificados al usuario por correo electrónico o mediante aviso en el panel administrativo, con una
        anticipación mínima de 15 días. El uso continuado del servicio tras la vigencia de los cambios implica
        la aceptación de los nuevos términos.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">11. Jurisdicción</h2>
      <p className="mb-4 leading-relaxed">
        Estos Términos y Condiciones se rigen por las leyes de la República Oriental del Uruguay. Cualquier
        controversia será sometida a la jurisdicción de los tribunales competentes de Uruguay.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-3 text-gray-800 dark:text-gray-100">12. Contacto</h2>
      <p className="mb-4 leading-relaxed">
        Para consultas sobre estos Términos y Condiciones podés contactarnos en:
      </p>
      <address className="not-italic mb-8 leading-relaxed">
        <strong>BrewControl</strong><br />
        Puntas de Valdez, San José, Uruguay<br />
        <a href="mailto:admin@brewcontrol.uy" className="underline text-primary-500">admin@brewcontrol.uy</a><br />
        <span className="text-sm text-gray-500 mt-2 block">Administrado por Leandro Curbelo · C.I. 5.048.194-3</span>
      </address>

    </main>
    <Footer />
  </>;
}
